/**
 * One line naming the device a model number resolved to.
 *
 * A lookup can return several models — an Apple A-number often spans chips or
 * generations — and the line must say so rather than pick one. Where the names
 * differ in a single bracketed detail, it is folded in place:
 *
 *   MacBook Pro (14-inch, M1 Max, 2021) + MacBook Pro (14-inch, M1 Pro, 2021)
 *     → MacBook Pro (14-inch, M1 Max or M1 Pro, 2021)
 *
 * When they differ in more than one detail, folding would imply combinations
 * that don't exist (a 16-inch M1 Pro from 14-inch M1 Pro + 16-inch M1 Max), so
 * the line falls back to the shared name and a count.
 */
import type { DeviceModel } from '@techgitter/contracts';

interface ParsedName {
  base: string;
  details: string[];
}

function parseName(name: string): ParsedName {
  const match = /^(.*?)\s*\((.*)\)\s*$/.exec(name);
  if (!match) return { base: name.trim(), details: [] };
  return { base: match[1].trim(), details: match[2].split(',').map(d => d.trim()) };
}

export function summariseDeviceModels(models: Pick<DeviceModel, 'name'>[]): string | null {
  const names = Array.from(new Set(models.map(m => (m.name ?? '').trim()).filter(Boolean)));
  if (names.length === 0) return null;
  if (names.length === 1) return names[0];

  const parsed = names.map(parseName);
  const base = parsed[0].base;
  const sameBase = parsed.every(p => p.base === base);
  const width = parsed[0].details.length;

  if (sameBase && width > 0 && parsed.every(p => p.details.length === width)) {
    const columns = Array.from({ length: width }, (_, i) =>
      Array.from(new Set(parsed.map(p => p.details[i]))),
    );
    if (columns.filter(c => c.length > 1).length === 1) {
      return `${base} (${columns.map(c => c.join(' or ')).join(', ')})`;
    }
  }

  return sameBase ? `${base} — one of ${names.length} models` : `One of ${names.length} models`;
}
